/* ===================================================================
   SynEra Science — premium 3D renders for HOW IT WORKS panels
   ===================================================================
   Three bespoke scenes, one per panel, rendered with Three.js r150:

     • PEPTIDE  — ball-and-stick molecular sculpture in rose-gold
                  metallic, slow Y-rotation, subtle atom breathing.
     • HYDRATE  — glass water sphere with true refraction (IOR 1.33)
                  surrounded by orbiting droplets, breathing scale.
     • PROTECT  — faceted icosahedral crystal with refractive glass
                  material and a wireframe halo, slow tumble.

   Behaviour:
     - Smooth eased mouse parallax (cursor inside panel ↔ tilt).
     - Continuous gentle auto-rotation; mouse adds tilt on top.
     - All three scenes render every frame (cheap — each is small).
     - Master loop pauses entirely when section is offscreen.
     - WebGL/Three.js failure leaves the SVG-less canvases empty
       (the page degrades gracefully without throwing).
   =================================================================== */

(() => {
  'use strict';

  if (typeof THREE === 'undefined') return;

  // WebGL probe — bail if unavailable
  try {
    const probe = document.createElement('canvas');
    if (!(probe.getContext('webgl2') || probe.getContext('webgl'))) return;
  } catch (e) { return; }

  /* -- Brand palette ------------------------------------------------ */
  const C = {
    accent:      0xC48E6E,  // rose gold
    accentSoft:  0xE6C9B3,  // softer rose
    bone:        0xF4ECE1,  // bone
    deepWarm:    0x2A1810,
  };

  /* -- Shared circular sprite for all dust/star points --------------- */
  /* Generates a soft round gradient texture so points render as glowing
     dots with feathered edges, not hard square pixels. Used by the
     PointsMaterial.map on every dust layer across all three scenes. */
  const STAR_SPRITE = (() => {
    const s = 64;
    const cnv = document.createElement('canvas');
    cnv.width = s; cnv.height = s;
    const ctx = cnv.getContext('2d');
    const g = ctx.createRadialGradient(s/2, s/2, 0, s/2, s/2, s/2);
    g.addColorStop(0,   'rgba(255,255,255,1.0)');
    g.addColorStop(0.4, 'rgba(255,255,255,0.45)');
    g.addColorStop(1,   'rgba(255,255,255,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, s, s);
    const tex = new THREE.CanvasTexture(cnv);
    if ('colorSpace' in tex) tex.colorSpace = THREE.SRGBColorSpace;
    return tex;
  })();

  /* -- SHARED STARFIELD BUILDER ------------------------------------ */
  /* Used by all three scenes so the stars look IDENTICAL across them.
     Two layers (near brighter / far dimmer) for depth via brightness.

     - `sizeAttenuation: false` → size is in screen pixels, so stars are
       the same dot size regardless of camera distance or scene geometry.
     - Stars are distributed on a SPHERICAL SHELL (not a cylinder) so
       they fill the entire frustum from any camera angle — fixes the
       "stars only fill half the canvas" issue when a camera tilts.

     Returns { group, near, far } so scenes can rotate the layers for
     parallax drift. The group is added to the SCENE (not the local
     group) so it doesn't tilt with mouse parallax. */
  function buildStarField(scene, density = 1.0) {
    // density param scales the star counts per scene — Hydrate uses a
    // smaller value because its wider frustum catches more of the shell
    // and would otherwise feel busier than the other panels.
    const NEAR = { count: Math.round(500 * density), innerR: 18, outerR: 32, size: 2.6, opacity: 0.95, color: C.accentSoft };
    const FAR  = { count: Math.round(800 * density), innerR: 30, outerR: 48, size: 1.7, opacity: 0.65, color: C.bone        };

    const make = (cfg) => {
      const positions = new Float32Array(cfg.count * 3);
      for (let i = 0; i < cfg.count; i++) {
        // Uniform random direction on a sphere (Marsaglia method)
        // — yields stars that fill the entire 360° view, not just a band.
        const u = Math.random() * 2 - 1;       // cos(theta), uniform on [-1,1]
        const phi = Math.random() * Math.PI * 2;
        const sinT = Math.sqrt(1 - u * u);
        const r = cfg.innerR + Math.random() * (cfg.outerR - cfg.innerR);
        positions[i * 3]     = sinT * Math.cos(phi) * r;
        positions[i * 3 + 1] = u                    * r;
        positions[i * 3 + 2] = sinT * Math.sin(phi) * r;
      }
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      const mat = new THREE.PointsMaterial({
        color:           cfg.color,
        size:            cfg.size,
        map:             STAR_SPRITE,
        alphaTest:       0.01,
        sizeAttenuation: false,                // <-- pixel-sized → identical across scenes
        transparent:     true,
        opacity:         cfg.opacity,
        toneMapped:      false,
        depthWrite:      false,
        blending:        THREE.AdditiveBlending,
      });
      return new THREE.Points(geo, mat);
    };

    const group = new THREE.Group();
    const near = make(NEAR);
    const far  = make(FAR);
    group.add(near);
    group.add(far);
    scene.add(group);
    return { group, near, far };
  }

  /* =================================================================
     SceneBase — shared renderer / camera / lights / mouse / loop
     ================================================================= */
  class SceneBase {
    constructor(canvas) {
      this.canvas = canvas;
      this.mouse  = { x: 0, y: 0 };
      this.target = { x: 0, y: 0 };

      this._setupRenderer();
      this.scene  = new THREE.Scene();
      this._setupCamera();
      this._setupLights();
      this._setupEnvironment();
      this.build();           // subclass implements geometry
      this._bindMouse();
      this._observeResize();
      this.resize();
    }

    /* ResizeObserver catches the case where a canvas was 0×0 at init
       (because its parent .fig-panel was display:none) and only gains
       real dimensions later when its tab becomes active. Without this,
       hidden tabs never render anything. */
    _observeResize() {
      if (typeof ResizeObserver === 'undefined') return;
      this._ro = new ResizeObserver(() => this.resize());
      this._ro.observe(this.canvas);
    }

    _setupRenderer() {
      this.renderer = new THREE.WebGLRenderer({
        canvas: this.canvas,
        antialias: true,
        alpha: true,
        powerPreference: 'high-performance',
      });
      this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      // r150 uses outputColorSpace, but be defensive for back-compat
      if ('outputColorSpace' in this.renderer) {
        this.renderer.outputColorSpace = THREE.SRGBColorSpace;
      } else if ('outputEncoding' in this.renderer) {
        this.renderer.outputEncoding = THREE.sRGBEncoding;
      }
      this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
      this.renderer.toneMappingExposure = 1.05;
    }

    _setupCamera() {
      this.camera = new THREE.PerspectiveCamera(35, 1, 0.1, 100);
      this.camera.position.set(0, 0, 6);
    }

    _setupLights() {
      // Soft warm ambient
      this.scene.add(new THREE.AmbientLight(0xFFEEDC, 0.32));

      // Rose-gold key from upper-front
      const key = new THREE.DirectionalLight(C.accentSoft, 1.45);
      key.position.set(2.5, 3, 4);
      this.scene.add(key);

      // Cool rim from behind for edge separation
      const rim = new THREE.DirectionalLight(0xA8C8E8, 0.85);
      rim.position.set(-3, 1.5, -2);
      this.scene.add(rim);

      // Soft bottom fill so things don't go black underneath
      const fill = new THREE.DirectionalLight(0xFFEEDC, 0.30);
      fill.position.set(0, -2, 1);
      this.scene.add(fill);

      // Warm point accent
      const pt = new THREE.PointLight(C.accent, 0.55, 14);
      pt.position.set(2, 2.5, 2);
      this.scene.add(pt);
    }

    /* Build a procedural equirectangular environment for reflections.
       Subclasses can opt out by overriding to a no-op. */
    _setupEnvironment() {
      const size = 256;
      const cnv = document.createElement('canvas');
      cnv.width = size; cnv.height = size;
      const ctx = cnv.getContext('2d');

      // Vertical warm gradient
      const grad = ctx.createLinearGradient(0, 0, 0, size);
      grad.addColorStop(0,    '#3A2A20');
      grad.addColorStop(0.55, '#1A0F0A');
      grad.addColorStop(1,    '#0B0807');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, size, size);

      // Bright spot for highlight catch
      const spot = ctx.createRadialGradient(
        size * 0.65, size * 0.30, 0,
        size * 0.65, size * 0.30, size * 0.55
      );
      spot.addColorStop(0,   'rgba(244,236,225,0.65)');
      spot.addColorStop(0.4, 'rgba(244,236,225,0.20)');
      spot.addColorStop(1,   'rgba(244,236,225,0)');
      ctx.fillStyle = spot;
      ctx.fillRect(0, 0, size, size);

      const tex = new THREE.CanvasTexture(cnv);
      tex.mapping = THREE.EquirectangularReflectionMapping;
      if ('colorSpace' in tex) {
        tex.colorSpace = THREE.SRGBColorSpace;
      } else if ('encoding' in tex) {
        tex.encoding = THREE.sRGBEncoding;
      }
      this.scene.environment = tex;
    }

    _bindMouse() {
      const onMove = (e) => {
        const r = this.canvas.getBoundingClientRect();
        const x = ((e.clientX - r.left) / r.width)  * 2 - 1;
        const y = ((e.clientY - r.top)  / r.height) * 2 - 1;
        this.target.x =  x;
        this.target.y = -y;
      };
      const onLeave = () => {
        this.target.x = 0;
        this.target.y = 0;
      };
      this.canvas.addEventListener('pointermove',  onMove,  { passive: true });
      this.canvas.addEventListener('pointerleave', onLeave, { passive: true });
    }

    build() { /* subclass override */ }

    resize() {
      const rect = this.canvas.getBoundingClientRect();
      const w = Math.max(rect.width  || 360, 1);
      const h = Math.max(rect.height || 360, 1);
      this.renderer.setSize(w, h, false);
      this.camera.aspect = w / h;
      this.camera.updateProjectionMatrix();
    }

    update(t) {
      // Eased follow toward target mouse position
      this.mouse.x += (this.target.x - this.mouse.x) * 0.06;
      this.mouse.y += (this.target.y - this.mouse.y) * 0.06;
    }

    render() {
      this.renderer.render(this.scene, this.camera);
    }
  }

  /* =================================================================
     PEPTIDE — ball-and-stick molecular sculpture
     ----------------------------------------------------------------
     Polished rose-gold metal matching the home-page DNA palette.
     Auto-fits camera so it never crops. Grabbable: mouse-drag rotates
     it freely, autoplay resumes on release.
     ================================================================= */
  class PeptideScene extends SceneBase {
    constructor(canvas) {
      super(canvas);
      this.dragging = false;
      this.dragLast = { x: 0, y: 0 };
      this.dragVelocity = { x: 0, y: 0 };
      // Camera padding factor — controls how much empty space sits
      // around the molecule. 1.10 = 10% padding (default for relax tab
      // and orbit-centre). Subclasses or instances can override before
      // calling resize() to zoom in (smaller value = closer camera).
      this._paddingFactor = 1.10;
      // boundingRadius is set inside build() based on actual atom
      // positions (peptide + counterions). Refit the camera now that
      // the geometry is in place.
      this.resize();
    }

    /* Override the base mouse binding — we want drag-to-rotate
       behaviour, with parallax only when NOT dragging. */
    _bindMouse() {
      const c = this.canvas;

      const getXY = (e) => {
        const r = c.getBoundingClientRect();
        return {
          x: ((e.clientX - r.left) / r.width)  * 2 - 1,
          y: ((e.clientY - r.top)  / r.height) * 2 - 1,
        };
      };

      const onMove = (e) => {
        const p = getXY(e);
        if (this.dragging) {
          // Drag delta — convert to rotation deltas (yaw + pitch)
          const dx = p.x - this.dragLast.x;
          const dy = p.y - this.dragLast.y;
          this.dragVelocity.x = dy * 1.6;   // pitch
          this.dragVelocity.y = dx * 1.6;   // yaw
          this.dragLast = p;
        } else {
          // Eased parallax target
          this.target.x =  p.x;
          this.target.y = -p.y;
        }
      };

      const onDown = (e) => {
        this.dragging = true;
        this.dragLast = getXY(e);
        c.setPointerCapture && c.setPointerCapture(e.pointerId);
        c.style.cursor = 'grabbing';
        // Reset parallax so it doesn't fight the drag
        this.target.x = 0; this.target.y = 0;
        this.mouse.x  = 0; this.mouse.y  = 0;
      };

      const onUp = (e) => {
        this.dragging = false;
        c.releasePointerCapture && c.releasePointerCapture(e.pointerId);
        c.style.cursor = 'grab';
      };

      const onLeave = () => {
        if (!this.dragging) { this.target.x = 0; this.target.y = 0; }
      };

      c.style.cursor = 'grab';
      c.addEventListener('pointermove',   onMove,  { passive: true });
      c.addEventListener('pointerdown',   onDown);
      c.addEventListener('pointerup',     onUp);
      c.addEventListener('pointercancel', onUp);
      c.addEventListener('pointerleave',  onLeave, { passive: true });
    }

    /* Auto-fit camera distance based on viewport aspect.
       Ensures the molecule (with its rotation bounding sphere)
       always fits inside the canvas. */
    resize() {
      const rect = this.canvas.getBoundingClientRect();
      const w = Math.max(rect.width  || 360, 1);
      const h = Math.max(rect.height || 360, 1);
      this.renderer.setSize(w, h, false);
      const aspect = w / h;
      this.camera.aspect = aspect;
      const fovRad = (this.camera.fov * Math.PI) / 180;
      const r = this.boundingRadius;
      const distH = r / Math.tan(fovRad / 2);                  // fits height
      const distW = r / (aspect * Math.tan(fovRad / 2));        // fits width
      this.camera.position.z = Math.max(distH, distW) * (this._paddingFactor || 1.10);
      this.camera.updateProjectionMatrix();
    }

    build() {
      this.group = new THREE.Group();
      this.scene.add(this.group);

      // 8 atoms in a curved peptide-chain arrangement (artistic, not literal)
      const positions = [
        [-1.85,  0.60,  0.20],
        [-1.05,  0.00, -0.30],
        [-0.20,  0.50,  0.10],
        [ 0.60, -0.10, -0.20],
        [ 1.45,  0.40,  0.30],
        [ 0.20, -1.00,  0.50],
        [-1.40, -0.70, -0.40],
        [ 1.80, -0.60, -0.10],
      ];

      // Polished rose-gold metal — matches the home-page DNA palette.
      // Pushed to mirror-finish for crisper highlights and richer
      // metallic depth across the burgundy→rose→champagne gradient.
      const atomMat = new THREE.MeshPhysicalMaterial({
        color:               0xC48E6E,    // rose gold mid-tone
        metalness:           1.0,         // pure metal
        roughness:           0.08,        // mirror-polish
        clearcoat:           1.0,         // full clear-coat layer
        clearcoatRoughness:  0.04,        // crisp highlight catch
        emissive:            0x5A2F1F,    // deep burgundy core glow
        emissiveIntensity:   0.20,
        envMapIntensity:     2.10,        // environment dominates highlights
        reflectivity:        1.0,
      });

      const atomGeo = new THREE.SphereGeometry(1, 48, 48);
      this.atoms = [];
      positions.forEach((pos, i) => {
        const baseR = 0.24 + (i % 3 === 0 ? 0.05 : 0);
        const m = new THREE.Mesh(atomGeo, atomMat);
        m.position.set(pos[0], pos[1], pos[2]);
        m.scale.setScalar(baseR);
        m.userData = { baseR, phase: i * 0.7 };
        this.group.add(m);
        this.atoms.push(m);
      });

      // Bonds — same metal family, slightly darker for depth
      const bondMat = new THREE.MeshPhysicalMaterial({
        color:             0x945A40,    // ROSE_SOFT from home-page DNA
        metalness:         1.0,
        roughness:         0.18,
        clearcoat:         0.85,
        clearcoatRoughness:0.10,
        emissive:          0x3A1F14,
        emissiveIntensity: 0.12,
        envMapIntensity:   1.6,
      });
      const bondPairs = [[0,1],[1,2],[2,3],[3,4],[1,6],[3,5],[4,7]];
      bondPairs.forEach(([a, b]) => {
        const v0 = new THREE.Vector3(...positions[a]);
        const v1 = new THREE.Vector3(...positions[b]);
        const dir = new THREE.Vector3().subVectors(v1, v0);
        const len = dir.length();
        const cyl = new THREE.CylinderGeometry(0.05, 0.05, len, 16, 1);
        const bond = new THREE.Mesh(cyl, bondMat);
        bond.position.copy(v0).addScaledVector(dir, 0.5);
        bond.quaternion.setFromUnitVectors(
          new THREE.Vector3(0, 1, 0),
          dir.clone().normalize()
        );
        this.group.add(bond);
      });

      // Starfield — shared helper, IDENTICAL params across all 3 scenes
      // (sizeAttenuation: false → pixel-sized dots, camera-distance independent)
      const sf = buildStarField(this.scene);
      this.dust      = sf.group;
      this._dustNear = sf.near;
      this._dustFar  = sf.far;

      // Two small acetate counterions floating below — diacetate salt
      // (separate molecules, not bonded to the peptide). Same rose-gold
      // family, smaller atoms + thinner bonds so they read as detail.
      this._addCounterions();

      // Centre the composition on origin using its BOUNDING BOX centre,
      // not its mean. The acetates bunch hard at the bottom so the mean
      // (centroid) gets pulled further down than visual symmetry wants,
      // leaving the peptide cluster sitting too high in the frame. Bbox
      // centring puts the topmost atom and bottommost atom equidistant
      // from origin — true visual symmetry — and also shrinks the
      // bounding sphere, which lets the camera fit closer.
      let minX = Infinity, minY = Infinity, minZ = Infinity;
      let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
      this.atoms.forEach((a) => {
        if (a.position.x < minX) minX = a.position.x;
        if (a.position.x > maxX) maxX = a.position.x;
        if (a.position.y < minY) minY = a.position.y;
        if (a.position.y > maxY) maxY = a.position.y;
        if (a.position.z < minZ) minZ = a.position.z;
        if (a.position.z > maxZ) maxZ = a.position.z;
      });
      const cx = (minX + maxX) / 2;
      const cy = (minY + maxY) / 2;
      const cz = (minZ + maxZ) / 2;
      this.group.children.forEach((child) => {
        child.position.x -= cx;
        child.position.y -= cy;
        child.position.z -= cz;
      });

      // Compute actual bounding radius from ALL atoms (peptide + counterions)
      // so the camera fits the whole composition during free rotation.
      let maxR = 0;
      this.atoms.forEach((a) => {
        const d = a.position.length() + (a.userData.baseR || 0.24) + 0.08;
        if (d > maxR) maxR = d;
      });
      this.boundingRadius = maxR;
    }

    /* Two acetate molecules (CH₃-COOH) drawn below the peptide, each as
       a 4-atom Y/triangle: methyl C → carboxyl C → =O above, -OH below.
       Half the atom size and half the bond thickness of the main peptide
       so they read as secondary detail. Added to this.atoms so they
       breathe in the update loop with distinct phase offsets. */
    _addCounterions() {
      const acetates = [
        { atoms: [
            [-1.10, -1.95,  0.05],   // CH₃
            [-0.55, -1.95,  0.00],   // carboxyl C
            [-0.40, -1.50,  0.10],   // =O
            [-0.40, -2.40, -0.05],   // -OH
          ] },
        { atoms: [
            [ 0.50, -1.95,  0.05],
            [ 1.05, -1.95,  0.00],
            [ 1.20, -1.50,  0.10],
            [ 1.20, -2.40, -0.05],
          ] },
      ];

      const atomMat = new THREE.MeshPhysicalMaterial({
        color:               0xC48E6E,
        metalness:           1.0,
        roughness:           0.10,
        clearcoat:           0.85,
        clearcoatRoughness:  0.06,
        emissive:            0x5A2F1F,
        emissiveIntensity:   0.18,
        envMapIntensity:     1.80,
        reflectivity:        0.9,
      });
      const bondMat = new THREE.MeshPhysicalMaterial({
        color:             0x945A40,
        metalness:         1.0,
        roughness:         0.20,
        clearcoat:         0.7,
        clearcoatRoughness:0.12,
        emissive:          0x3A1F14,
        emissiveIntensity: 0.10,
        envMapIntensity:   1.4,
      });

      const atomGeo = new THREE.SphereGeometry(1, 24, 24);
      const SMALL_R = 0.13;        // ~half of main atom (0.24)
      const SMALL_BOND_R = 0.025;  // ~half of main bond (0.05)

      acetates.forEach((ace, idx) => {
        ace.atoms.forEach((pos, i) => {
          const m = new THREE.Mesh(atomGeo, atomMat);
          m.position.set(pos[0], pos[1], pos[2]);
          m.scale.setScalar(SMALL_R);
          m.userData = { baseR: SMALL_R, phase: 8 + idx * 4 + i * 0.4 };
          this.group.add(m);
          this.atoms.push(m);
        });
        // Bonds: methyl→carboxyl, carboxyl→=O, carboxyl→-OH
        const bonds = [[0, 1], [1, 2], [1, 3]];
        bonds.forEach(([a, b]) => {
          const v0 = new THREE.Vector3(...ace.atoms[a]);
          const v1 = new THREE.Vector3(...ace.atoms[b]);
          const dir = new THREE.Vector3().subVectors(v1, v0);
          const len = dir.length();
          const cyl = new THREE.CylinderGeometry(SMALL_BOND_R, SMALL_BOND_R, len, 10, 1);
          const bond = new THREE.Mesh(cyl, bondMat);
          bond.position.copy(v0).addScaledVector(dir, 0.5);
          bond.quaternion.setFromUnitVectors(
            new THREE.Vector3(0, 1, 0),
            dir.clone().normalize()
          );
          this.group.add(bond);
        });
      });
    }

    update(t) {
      super.update(t);

      if (this.dragging) {
        // Direct drag rotation
        this.group.rotation.x += this.dragVelocity.x;
        this.group.rotation.y += this.dragVelocity.y;
        // Decay drag velocity each frame so motion stops with input
        this.dragVelocity.x *= 0.85;
        this.dragVelocity.y *= 0.85;
      } else {
        // Continue last drag velocity for a moment (momentum)
        if (Math.abs(this.dragVelocity.x) > 0.0005 || Math.abs(this.dragVelocity.y) > 0.0005) {
          this.group.rotation.x += this.dragVelocity.x;
          this.group.rotation.y += this.dragVelocity.y;
          this.dragVelocity.x *= 0.92;
          this.dragVelocity.y *= 0.92;
        }
        // Auto rotation (always running when not dragging)
        this.group.rotation.y += 0.0028;
        // Eased parallax tilt (X and Z so it doesn't fight Y auto-spin)
        const tx = this.mouse.y * 0.30;
        const tz = this.mouse.x * 0.18;
        this.group.rotation.x += (tx - this.group.rotation.x) * 0.05;
        this.group.rotation.z += (tz - this.group.rotation.z) * 0.05;
      }

      // Atoms breathe individually
      this.atoms.forEach((a) => {
        const s = 1 + Math.sin(t * 0.0014 + a.userData.phase) * 0.045;
        a.scale.setScalar(a.userData.baseR * s);
      });

      // Dust drift — two layers at different speeds for parallax depth
      this._dustNear.rotation.y -= 0.00040;
      this._dustNear.rotation.x += 0.00012;
      this._dustFar.rotation.y  += 0.00018;
      this._dustFar.rotation.x  -= 0.00006;
    }
  }

  /* =================================================================
     PEPTIDE CENTER — same molecule, no mouse interaction
     ----------------------------------------------------------------
     Subclass of PeptideScene used inside the orbit-center button on
     §04 Ensemble. Mouse is intentionally inert: the parent button
     handles click/hover to open the info card, and the canvas itself
     just gently auto-spins. No drag, no parallax tilt.
     ================================================================= */
  class PeptideCenterScene extends PeptideScene {
    /* Override drag/parallax binding from PeptideScene with a no-op.
       Pointer events should fall through to the button anyway (CSS
       sets pointer-events:none on the canvas), but defensively we
       also avoid registering any handlers in case CSS is overridden. */
    _bindMouse() { /* intentionally empty */ }

    /* Strip mouse-driven rotation; keep auto-spin + atom breathing +
       dust drift. We deliberately skip super.update() so the parent's
       mouse-target lerp never runs. */
    update(t) {
      // Gentle auto-rotation around Y, identical speed to the relax-tab version.
      this.group.rotation.y += 0.0028;

      // Atoms breathe individually
      this.atoms.forEach((a) => {
        const s = 1 + Math.sin(t * 0.0014 + a.userData.phase) * 0.045;
        a.scale.setScalar(a.userData.baseR * s);
      });

      // Dust drift — two layers at different speeds for parallax depth
      this._dustNear.rotation.y -= 0.00040;
      this._dustNear.rotation.x += 0.00012;
      this._dustFar.rotation.y  += 0.00018;
      this._dustFar.rotation.x  -= 0.00006;
    }
  }

  /* =================================================================
     HYDRATE — rose-gold droplet falls and triggers ring ripples
     ----------------------------------------------------------------
     A solid rose-gold teardrop falls from above and impacts the
     surface in a brief squish/spread before turning into a slowly-
     expanding ring ripple. Drop ALWAYS spawns at center for
     compositional consistency. Same metal material as Peptide atoms.
     Soft radial glow underneath; starfield dust drifting in the
     background (matches Peptide).
     ================================================================= */
  class HydrateScene extends SceneBase {
    constructor(canvas) {
      super(canvas);
      this.boundingRadius = 7.5;   // scaled up further → animation fills more canvas
      this.resize();
    }

    /* Camera at a SHALLOWER angle so the rings read as 3D wave crests
       (the y-bob shows in profile) instead of flat horizontal hoops.
       ~25° above horizontal — like looking across a still pool. */
    _setupCamera() {
      this.camera = new THREE.PerspectiveCamera(40, 1, 0.1, 100);
      this.camera.position.set(0, 2.6, 6.4);
      this.camera.lookAt(0, 0, 0);
    }

    resize() {
      const rect = this.canvas.getBoundingClientRect();
      const w = Math.max(rect.width  || 360, 1);
      const h = Math.max(rect.height || 360, 1);
      this.renderer.setSize(w, h, false);
      const aspect = w / h;
      this.camera.aspect = aspect;
      const fovRad = (this.camera.fov * Math.PI) / 180;
      const r = this.boundingRadius;
      const distH = r / Math.tan(fovRad / 2);
      const distW = r / (aspect * Math.tan(fovRad / 2));
      const dist  = Math.max(distH, distW) * 1.30;
      const angle = Math.atan2(this.camera.position.z, this.camera.position.y) || 0.85;
      this.camera.position.set(0, dist * Math.cos(angle), dist * Math.sin(angle));
      this.camera.lookAt(0, 0, 0);
      this.camera.updateProjectionMatrix();
    }

    _bindMouse() {
      const c = this.canvas;
      this._dropQueue = [];
      // Click anywhere → drop spawns at CENTER (ignore click position
      // for design consistency)
      c.addEventListener('pointerdown', () => {
        this._dropQueue.push(new THREE.Vector3(0, 0, 0));
      });
      c.style.cursor = 'pointer';
    }

    /* True teardrop silhouette — the iconic 💧 shape:
         - Pointed tip at the top (cosine taper, smooth-tangent at the
           belly so there's no kink)
         - Bulbous belly with widest part BELOW centre (not at it)
         - Rounded spherical bottom (ellipse section, smooth-tangent
           at the belly)
       This reads as "water drop" much more clearly than the previous
       symmetric egg. Built by warping a sphere mesh per-vertex. */
    _buildTeardropGeo(radius) {
      const geo = new THREE.SphereGeometry(1, 96, 96);   // bumped tessellation for cleaner curve
      const pos = geo.attributes.position;
      // Drop coordinate space anchors:
      const TIP    =  1.90;    // top apex (pointed)
      const WIDEST = -0.25;    // y of widest belly (below centre)
      const BOT    = -0.85;    // bottom apex (round bulb)
      // Map sphere y [-1, 1] → drop coordinate newY [BOT, TIP]
      const halfRange = (TIP - BOT) / 2;       // = 1.375
      const center    = (TIP + BOT) / 2;       // = 0.525
      for (let i = 0; i < pos.count; i++) {
        const x = pos.getX(i);
        const y = pos.getY(i);
        const z = pos.getZ(i);
        const newY = center + y * halfRange;
        // Profile r(newY): two pieces, smooth-tangent at WIDEST.
        let targetR;
        if (newY > WIDEST) {
          // Top: cosine taper — sharp at tip, horizontal tangent at belly
          const t = (newY - WIDEST) / (TIP - WIDEST);   // 0 at belly, 1 at tip
          targetR = Math.cos(t * Math.PI / 2);
        } else {
          // Bottom: ellipse section — round bulb, horizontal tangent at belly
          const t = (WIDEST - newY) / (WIDEST - BOT);   // 0 at belly, 1 at apex
          targetR = Math.sqrt(Math.max(0, 1 - t * t));
        }
        // Apply targetR while preserving the vertex's angular position
        const curR = Math.sqrt(x * x + z * z);
        if (curR > 1e-5) {
          const s = targetR / curR;
          pos.setX(i, x * s);
          pos.setZ(i, z * s);
        }
        pos.setY(i, newY);
      }
      pos.needsUpdate = true;
      geo.computeVertexNormals();
      geo.scale(radius, radius, radius);
      return geo;
    }

    /* Procedural radial-gradient glow texture for the soft pool light */
    _buildGlowTexture() {
      const size = 256;
      const cnv = document.createElement('canvas');
      cnv.width = size; cnv.height = size;
      const ctx = cnv.getContext('2d');
      const c = size / 2;
      const g = ctx.createRadialGradient(c, c, 0, c, c, c);
      g.addColorStop(0,    'rgba(196,142,110,0.32)');  // rose-gold center
      g.addColorStop(0.30, 'rgba(106,58,40,0.18)');    // mid burgundy
      g.addColorStop(0.65, 'rgba(26,15,10,0.08)');     // soft fall-off
      g.addColorStop(1,    'rgba(0,0,0,0)');           // fully transparent
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, size, size);
      const tex = new THREE.CanvasTexture(cnv);
      if ('colorSpace' in tex) tex.colorSpace = THREE.SRGBColorSpace;
      else if ('encoding' in tex) tex.encoding = THREE.sRGBEncoding;
      return tex;
    }

    /* Starfield helper — same as Peptide (near + far layers) */
    _buildPointLayer({ count, innerR, outerR, ySpread, size, opacity, color }) {
      const positions = new Float32Array(count * 3);
      for (let i = 0; i < count; i++) {
        const r = innerR + Math.random() * (outerR - innerR);
        const a = Math.random() * Math.PI * 2;
        const phi = (Math.random() - 0.5) * ySpread;
        positions[i * 3]     = Math.cos(a) * r;
        positions[i * 3 + 1] = phi;
        positions[i * 3 + 2] = Math.sin(a) * r;
      }
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      const mat = new THREE.PointsMaterial({
        color, size,
        map: STAR_SPRITE,
        alphaTest: 0.01,
        sizeAttenuation: true,
        transparent: true,
        opacity,
        toneMapped: false,           // bypass ACES so stars stay BRIGHT
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      });
      return new THREE.Points(geo, mat);
    }

    build() {
      this.group = new THREE.Group();
      this.scene.add(this.group);

      // === Same polished rose-gold metal as the Peptide atoms ===
      const metalMat = new THREE.MeshPhysicalMaterial({
        color:               0xC48E6E,
        metalness:           1.0,
        roughness:           0.08,
        clearcoat:           1.0,
        clearcoatRoughness:  0.04,
        emissive:            0x5A2F1F,
        emissiveIntensity:   0.20,
        envMapIntensity:     2.10,
        reflectivity:        1.0,
      });

      // === Ring pool — slimmer tube + bigger pool to support multi-ring
      //     impacts (each splash now spawns a wake of 4 staggered rings).
      //     MAX_RADIUS scaled up from 3.2 → 6.2 so the wake fills more
      //     of the canvas. Tube thickness bumped to compensate for the
      //     larger camera distance (so rings still read as 3D). ===
      const ringGeo = new THREE.TorusGeometry(1, 0.075, 20, 112);
      // Pool sized for sustained spam-click load: each drop fires a
      // 4-ring primary wake + 2-ring secondary (jet) wake = 6 slots per
      // event. 64 slots = ~10 simultaneous events fully buffered, far
      // beyond what a human can click in one breath. The recycler
      // never steals visible rings — under saturation, extra spawns
      // are silently dropped instead.
      this.RING_COUNT     = 64;
      this.MAX_RADIUS     = 6.2;
      this.RING_LIFETIME  = 9.5;
      this.rings = [];
      for (let i = 0; i < this.RING_COUNT; i++) {
        const m = new THREE.Mesh(ringGeo, metalMat.clone());
        m.material.transparent = true;
        m.material.opacity = 0;
        // Transparent rings at the same Y plane were depth-sort flickering
        // near end-of-life (when opacities cluster near zero). depthWrite
        // off on transparent meshes is the standard fix — Three.js still
        // sorts back-to-front, just doesn't write the depth buffer that
        // was triggering the flicker.
        m.material.depthWrite = false;
        m.rotation.x = Math.PI / 2;
        m.scale.setScalar(0);
        m.userData = {
          age:       this.RING_LIFETIME + 1,
          tubeScale: 1.0,
          maxOpacity:1.0,
        };
        this.group.add(m);
        this.rings.push(m);
      }

      // === Falling droplet pool — each drop has its own material so
      //     it can fade out independently during the impact animation.
      //     Drop world size = 0.40 (was 0.21 originally) — scaled by
      //     26.8/14.3 = 1.87 so it has the SAME APPARENT SIZE on screen
      //     after the camera pulled back to fit the larger ring system. ===
      const dropGeo = this._buildTeardropGeo(0.40);
      this.DROP_COUNT          = 6;
      this.DROP_SPAWN_HEIGHT   = 6.5;   // higher spawn — drop falls a longer arc
      this.GRAVITY             = -7.5;
      this.DROP_AUTO_INTERVAL  = 7.0;     // ambient drop every 7s — gives the
                                          // primary ripples + Worthington jet
                                          // time to fully play out and settle
                                          // before the next event
      this.IMPACT_DURATION     = 0.32;    // squish / spread / fade duration
      this.FADE_IN_DURATION    = 0.55;    // fade-in time at start of fall
      this._lastDropSpawn      = -100;    // far in the past → first frame triggers spawn
      this._currentTime        = 0;
      this.drops = [];
      for (let i = 0; i < this.DROP_COUNT; i++) {
        const d = new THREE.Mesh(dropGeo, metalMat.clone());
        d.material.transparent = true;
        d.material.opacity = 0;            // start invisible — fades in
        d.material.depthWrite = false;
        d.visible = false;
        d.userData = { active: false, vy: 0, impacting: false, impactT: 0, spawnT: 0 };
        this.group.add(d);
        this.drops.push(d);
      }

      // === Worthington jet — the iconic small "rebound bead" that pops
      //     UPWARD from the impact point in slow-motion water-drop videos.
      //     This is the single biggest visual cue people associate with
      //     "drop hits water." Smaller than the main drop, launches with
      //     positive vy, falls back under gravity, triggers a soft
      //     secondary ripple on landing. ===
      const jetGeo = this._buildTeardropGeo(0.16);   // smaller bead
      this.JET_COUNT       = 6;
      this.JET_LAUNCH_VY   = 4.6;          // initial upward velocity
      this.JET_DELAY       = 0.10;         // appears 100ms after impact
                                           // (after the splash crown forms)
      this.jets = [];
      for (let i = 0; i < this.JET_COUNT; i++) {
        const j = new THREE.Mesh(jetGeo, metalMat.clone());
        j.material.transparent = true;
        j.material.opacity = 0;
        j.material.depthWrite = false;
        j.visible = false;
        j.userData = {
          active: false,
          vy: 0,
          ascending: true,
          launchT: 0,            // scheduled launch time
          impactDone: false,
        };
        this.group.add(j);
        this.jets.push(j);
      }

      // === Soft radial glow under the pool — replaces the hard disc ===
      const glowMat = new THREE.MeshBasicMaterial({
        map:         this._buildGlowTexture(),
        transparent: true,
        depthWrite:  false,
        blending:    THREE.NormalBlending,
      });
      this.glow = new THREE.Mesh(new THREE.PlaneGeometry(16.5, 16.5), glowMat);
      this.glow.rotation.x = -Math.PI / 2;
      this.glow.position.y = -0.005;
      this.group.add(this.glow);

      // Starfield — shared helper, but Hydrate uses a lower density
      // because its wider frustum (camera pulled back for the bigger
      // ring system) catches more of the spherical shell than the
      // tighter Relax/Protect framings.
      const sf = buildStarField(this.scene, 0.5);
      this.dust      = sf.group;
      this._dustNear = sf.near;
      this._dustFar  = sf.far;

      this._lastT = 0;
    }

    _spawnDrop() {
      // Always at center
      let drop = this.drops.find(d => !d.userData.active);
      if (!drop) drop = this.drops.reduce((a, b) => a.position.y > b.position.y ? a : b);
      drop.position.set(0, this.DROP_SPAWN_HEIGHT, 0);
      drop.userData.active    = true;
      drop.userData.vy        = 0;
      drop.userData.impacting = false;
      drop.userData.impactT   = 0;
      drop.userData.spawnT    = this._currentTime;   // track for fade-in
      drop.material.opacity   = 0;                    // start invisible
      drop.scale.set(1, 1, 1);
      drop.visible = true;
    }

    /* Schedule a Worthington jet — the small rebound bead that shoots
       upward from the impact point. Schedules with JET_DELAY so it
       appears AFTER the splash crown forms (matches real water physics). */
    _spawnJet() {
      let jet = this.jets.find(j => !j.userData.active);
      if (!jet) jet = this.jets[0];
      jet.userData.active     = true;
      jet.userData.vy         = this.JET_LAUNCH_VY;
      jet.userData.ascending  = true;
      jet.userData.launchT    = this._currentTime + this.JET_DELAY;
      jet.userData.impactDone = false;
      jet.position.set(0, 0, 0);
      jet.scale.set(1, 1, 1);
      jet.material.opacity = 0;
      jet.visible = false;                     // shows when launchT reached
    }

    /* Smaller, softer ring wake fired when the Worthington jet falls
       back to the surface. Half the amplitude/opacity of the primary
       wake so it reads as a secondary, smaller splash. */
    _spawnSecondaryWake(pos) {
      const wake = [
        [ 0.05, 0.85, 0.55, 0.20 ],
        [-0.12, 0.55, 0.40, 0.10 ],
      ];
      wake.forEach(([ageOffset, tube, opacity, startR]) => {
        const ring = this._findOldestRing();
        if (!ring) return;   // pool saturated — drop this ring rather than steal a visible one
        ring.userData.age        = ageOffset;
        ring.userData.tubeScale  = tube;
        ring.userData.maxOpacity = opacity;
        ring.userData.startR     = startR;
        ring.position.set(pos.x, 0, pos.z);
        ring.scale.set(startR, startR, tube);
        ring.material.opacity = 0;
      });
    }

    /* Find an expired ring slot for recycling. Returns null if every
       ring in the pool is still live — the caller is expected to skip
       the spawn in that case. This is the core fix for the
       "rings glitching under spam-click" bug: the previous version
       blindly grabbed `max(age)`, which under heavy spam meant
       stealing the OLDEST live ring (the largest/outermost one,
       since rings grow with age), popping it out of frame. With a
       generously sized pool plus drop-on-overflow, the visible
       rings are never disturbed — extra spam clicks just don't
       spawn rings. */
    _findOldestRing() {
      let bestExpired = null;
      for (let i = 0; i < this.rings.length; i++) {
        const r = this.rings[i];
        if (r.userData.age > this.RING_LIFETIME) {
          if (!bestExpired || r.userData.age > bestExpired.userData.age) bestExpired = r;
        }
      }
      return bestExpired;   // may be null if pool is saturated
    }

    /* On impact, spawn a STAGGERED WAKE of 4 rings — each at a slightly
       different start time and tube thickness. Primary ring starts AT
       impact (no delay); trailing rings cascade tightly within 0.36s
       so the wake reads as a fast-developing wave system. */
    _spawnRing(pos, big) {
      // Each entry: [ageOffset, tubeScale, maxOpacity, startR]
      // startR defines the radius the ring begins at — the PRIMARY ring
      // starts at the drop's footprint (0.45) so it grows OUT of where
      // the drop is, not from a tiny dot in the middle. Trailing rings
      // start small for a true "wake" cascade behind it.
      const wake = [
        [ 0.05, 1.55, 1.00, 0.45 ],   // primary — starts at drop radius
        [-0.10, 1.10, 0.85, 0.20 ],   // secondary
        [-0.22, 0.78, 0.65, 0.10 ],   // tertiary
        [-0.36, 0.55, 0.45, 0.05 ],   // small trailing inner ring
      ];
      wake.forEach(([ageOffset, tube, opacity, startR]) => {
        const ring = this._findOldestRing();
        if (!ring) return;   // pool saturated — drop this ring rather than steal a visible one
        ring.userData.age        = ageOffset;
        ring.userData.tubeScale  = tube;
        ring.userData.maxOpacity = opacity;
        ring.userData.startR     = startR;
        ring.position.set(pos.x, 0, pos.z);
        ring.scale.set(startR, startR, tube);
        ring.material.opacity = 0;
      });
    }

    update(t) {
      super.update(t);
      const tSec = t * 0.001;
      const dt   = Math.min(0.05, tSec - (this._lastT || tSec));
      this._lastT = tSec;
      this._currentTime = tSec;

      // Process click queue
      while (this._dropQueue.length > 0) {
        this._dropQueue.shift();
        this._spawnDrop();
        this._lastDropSpawn = tSec;
      }

      // Ambient drop on a cycle
      if (tSec - this._lastDropSpawn > this.DROP_AUTO_INTERVAL) {
        this._lastDropSpawn = tSec;
        this._spawnDrop();
      }

      // Animate drops (falling + impact)
      this.drops.forEach((d) => {
        if (!d.userData.active) return;

        if (d.userData.impacting) {
          // ===== Impact animation: squish, spread, fade =====
          d.userData.impactT += dt;
          const ip = d.userData.impactT / this.IMPACT_DURATION;
          if (ip >= 1) {
            d.userData.active    = false;
            d.userData.impacting = false;
            d.visible            = false;
            d.scale.set(1, 1, 1);
            d.material.opacity   = 1;
            return;
          }
          // Drop "becomes" the ring: gently flatten + spread, fading out
          // while the primary ring grows from the drop's footprint. The
          // small spread (1 → 1.7) is tuned so the drop's outer edge
          // tracks the ring's expansion through the impact window —
          // hands off cleanly instead of vanishing then a separate
          // ring popping in.
          const ease = ip * ip;
          const sX = 1 + ease * 0.7;            // gentle outward spread
          const sY = Math.max(0.04, 1 - ip * 0.95); // flatten
          d.scale.set(sX, sY, sX);
          d.position.y = 0;                     // hold at surface
          // Fade ease — slightly slower than linear so the drop is still
          // partly visible as the ring grows past it
          d.material.opacity = Math.pow(1 - ip, 1.4);
          return;
        }

        // ===== Free-fall =====
        d.userData.vy   += this.GRAVITY * dt;
        d.position.y    += d.userData.vy * dt;
        // Vertical stretch as drop accelerates — more dramatic now
        // (1.0 → ~1.6 by impact) so the drop reads as an elongated
        // teardrop just before it hits, the way real falling water does.
        const speed = Math.abs(d.userData.vy);
        const stretch = Math.min(1.6, 1 + speed * 0.06);
        // Subtle horizontal squeeze as drop stretches vertically
        // (volume conservation feel — the drop pinches as it elongates)
        const squeeze = Math.max(0.85, 1 / Math.sqrt(stretch));
        d.scale.set(squeeze, stretch, squeeze);

        // Fade in over the first part of the fall — drop materialises
        // out of the air rather than popping into existence
        const sinceSpawn = tSec - d.userData.spawnT;
        const fadeIn = Math.min(1, sinceSpawn / this.FADE_IN_DURATION);
        d.material.opacity = fadeIn;

        if (d.position.y <= 0) {
          // Begin impact animation; spawn ring at impact point now
          d.position.y         = 0;
          d.userData.impacting = true;
          d.userData.impactT   = 0;
          this._spawnRing(d.position, true);
          this._spawnJet();        // schedule the rebound bead
        }
      });

      // ===== Worthington jets — rebound beads ascending then falling =====
      this.jets.forEach((j) => {
        if (!j.userData.active) return;
        // Wait for scheduled launch time before becoming visible
        if (tSec < j.userData.launchT) return;
        if (!j.visible) {
          j.visible = true;
          j.position.y = 0;
        }
        // Apply gravity
        j.userData.vy += this.GRAVITY * dt;
        j.position.y  += j.userData.vy * dt;
        // Fade in at launch (first 100ms), then full opacity; fade tip
        // slightly as it nears the peak so it dissolves into the air
        const sinceLaunch = tSec - j.userData.launchT;
        const fadeIn      = Math.min(1, sinceLaunch / 0.10);
        j.material.opacity = fadeIn;
        // Slight elongation as it ascends/falls — same teardrop physics
        const jetSpeed   = Math.abs(j.userData.vy);
        const jetStretch = Math.min(1.5, 1 + jetSpeed * 0.07);
        const jetSqueeze = Math.max(0.85, 1 / Math.sqrt(jetStretch));
        j.scale.set(jetSqueeze, jetStretch, jetSqueeze);

        // When jet falls back to surface → small secondary ripple, retire
        if (!j.userData.impactDone && j.userData.vy < 0 && j.position.y <= 0) {
          j.userData.impactDone = true;
          j.userData.active     = false;
          j.visible             = false;
          this._spawnSecondaryWake(new THREE.Vector3(0, 0, 0));
        }
      });

      // Animate every ring in the pool — slow expansion + per-ring tube
      // thickness, fade-in for negative ages, and a subtle y-bob so the
      // ring rises slightly at midlife and sinks at the end (mimics a
      // water surface lifting under wave crest, then settling).
      //
      // Two opacity envelopes layered here:
      //   1. Birth fade-in (first 4% of life) — masks the appearance
      //      pop of the staggered cascade rings (secondary/tertiary/
      //      quaternary born 100–360ms after the primary). Without
      //      this, each cascade ring would suddenly materialise at
      //      full opacity inside the still-expanding primary, reading
      //      as a glitch instead of an emerging sub-ripple.
      //   2. Late-life fade-out (after 55% of life) — original.
      this.rings.forEach((ring) => {
        ring.userData.age += dt;
        const t01 = ring.userData.age / this.RING_LIFETIME;

        if (t01 < 0) {
          ring.material.opacity = 0;
          return;
        }
        if (t01 > 1) {
          ring.material.opacity = 0;
          return;
        }

        // Radial expansion (ease-out) — starts from per-ring startR so
        // the primary ring grows out of the drop's footprint instead of
        // popping into existence as a tiny dot at the centre.
        const ease   = 1 - Math.pow(1 - t01, 1.8);
        const startR = ring.userData.startR != null ? ring.userData.startR : 0.10;
        const r      = startR + ease * (this.MAX_RADIUS - startR);
        // Per-ring tube thickness from spawn config + slight growth
        const tube = ring.userData.tubeScale * (1 + t01 * 0.4);
        ring.scale.set(r, r, tube);
        // Y-bob: ring rises as a wave crest at midlife, sinks at the end
        const bobUp   = Math.sin(t01 * Math.PI) * 0.22;
        const bobSink = -Math.pow(t01, 3) * 0.08;
        ring.position.y = bobUp + bobSink;

        // Opacity envelope: birth fade-in × late-life fade-out
        let op = ring.userData.maxOpacity;
        const FADE_IN_FRAC = 0.04;                       // ~380ms at LIFETIME=9.5
        if (t01 < FADE_IN_FRAC) op *= t01 / FADE_IN_FRAC;
        if (t01 > 0.55)         op *= 1 - (t01 - 0.55) / 0.45;
        ring.material.opacity = op;
      });

      // Starfield dust drifts in opposite directions for depth parallax
      this._dustNear.rotation.y -= 0.00040;
      this._dustNear.rotation.x += 0.00012;
      this._dustFar.rotation.y  += 0.00018;
      this._dustFar.rotation.x  -= 0.00006;

      // Subtle camera horizontal drift
      this.camera.position.x = Math.sin(tSec * 0.10) * 0.18;
      this.camera.lookAt(0, 0, 0);
    }
  }


  /* =================================================================
     PROTECT — single faceted rose-gold gem (luxury preservation)
     ----------------------------------------------------------------
     A polished icosahedral gem in rose-gold metal, slowly turning so
     light catches different facets. Mouse drag rotates it with
     momentum (same controls as Peptide). Reads instantly as "valuable
     thing being preserved" — the universal symbol of luxury beauty
     packaging.
     ================================================================= */
  class ProtectScene extends SceneBase {
    constructor(canvas) {
      super(canvas);
      this.dragging     = false;
      this.dragLast     = { x: 0, y: 0 };
      this.dragVelocity = { x: 0, y: 0 };
      this.boundingRadius = 1.6;
      this.resize();
    }

    _setupCamera() {
      this.camera = new THREE.PerspectiveCamera(35, 1, 0.1, 100);
      this.camera.position.set(0, 0, 6);
    }

    resize() {
      const rect = this.canvas.getBoundingClientRect();
      const w = Math.max(rect.width  || 360, 1);
      const h = Math.max(rect.height || 360, 1);
      this.renderer.setSize(w, h, false);
      const aspect = w / h;
      this.camera.aspect = aspect;
      const fovRad = (this.camera.fov * Math.PI) / 180;
      const r = this.boundingRadius;
      const distH = r / Math.tan(fovRad / 2);
      const distW = r / (aspect * Math.tan(fovRad / 2));
      this.camera.position.z = Math.max(distH, distW) * 1.20;
      this.camera.updateProjectionMatrix();
    }

    /* Click-only — no drag rotation. Cursor over canvas does parallax
       tilt; click triggers an impact ripple at the touched gem point. */
    _bindMouse() {
      const c = this.canvas;
      const raycaster = new THREE.Raycaster();
      const ndc = new THREE.Vector2();
      this._impactQueue = [];

      const onMove = (e) => {
        const r = c.getBoundingClientRect();
        const x = ((e.clientX - r.left) / r.width)  * 2 - 1;
        const y = ((e.clientY - r.top)  / r.height) * 2 - 1;
        this.target.x =  x;
        this.target.y = -y;
      };

      const onLeave = () => {
        this.target.x = 0;
        this.target.y = 0;
      };

      const onDown = (e) => {
        // Click handler: raycast to find gem hits and fire the
        // impact ripple shader effect on actual mesh hits.
        const r = c.getBoundingClientRect();
        ndc.x =  ((e.clientX - r.left) / r.width)  * 2 - 1;
        ndc.y = -((e.clientY - r.top)  / r.height) * 2 + 1;
        raycaster.setFromCamera(ndc, this.camera);
        const hits = raycaster.intersectObject(this.gem, false);
        if (hits.length > 0) {
          const local = this.gem.worldToLocal(hits[0].point.clone());
          this._impactQueue.push({ pos: local, time: this._currentTime });
        }
      };

      c.style.cursor = 'pointer';
      c.addEventListener('pointermove',  onMove,  { passive: true });
      c.addEventListener('pointerleave', onLeave, { passive: true });
      c.addEventListener('pointerdown',  onDown);
    }

    /* High-contrast procedural studio environment — many bright spots
       on a warm mid-dark backdrop so every facet of the gem catches
       a strong reflection no matter which way it turns. Bigger and
       brighter than the original to push the metallic read. */
    _setupEnvironment() {
      const size = 1024;
      const cnv = document.createElement('canvas');
      cnv.width = size; cnv.height = size;
      const ctx = cnv.getContext('2d');

      // Backdrop — stays in the brand deep-warm range so reflections
      // don't pick up any neutral grey or white. Slightly darker than
      // before so the gem reads with more contrast and depth.
      const grad = ctx.createLinearGradient(0, 0, 0, size);
      grad.addColorStop(0,    '#553620');
      grad.addColorStop(0.55, '#1F100B');
      grad.addColorStop(1,    '#0B0807');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, size, size);

      // Studio softbox + rim spots — all tinted to the brand rose-gold
      // family so reflections READ as rose-gold no matter which face
      // catches a highlight. (Previously the cores were pure white,
      // which washed the rose tint out of the gem.)
      const spots = [
        // Main key (top right) — soft cream-rose, large
        { x: 0.62, y: 0.18, r: 0.58, c: 'rgba(244,220,196,0.95)' },
        // Cross fill (top left) — softer, slightly cooler cream
        { x: 0.22, y: 0.26, r: 0.46, c: 'rgba(232,210,188,0.85)' },
        // Lower side rim (right) — deeper rose
        { x: 0.85, y: 0.58, r: 0.36, c: 'rgba(218,170,138,0.85)' },
        // Lower side rim (left) — soft rose
        { x: 0.10, y: 0.62, r: 0.34, c: 'rgba(230,196,170,0.78)' },
        // Top centre catch (small + intense) — warm cream
        { x: 0.50, y: 0.08, r: 0.22, c: 'rgba(248,228,206,0.95)' },
        // Underside warm bounce — saturated rose-gold
        { x: 0.50, y: 0.92, r: 0.38, c: 'rgba(196,142,110,0.70)' },
      ];
      spots.forEach(s => {
        const g = ctx.createRadialGradient(
          s.x * size, s.y * size, 0,
          s.x * size, s.y * size, s.r * size
        );
        g.addColorStop(0,    s.c);
        g.addColorStop(0.45, s.c.replace(/[\d.]+\)$/, '0.35)'));
        g.addColorStop(1,    'rgba(0,0,0,0)');
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, size, size);
      });

      const tex = new THREE.CanvasTexture(cnv);
      tex.mapping = THREE.EquirectangularReflectionMapping;
      if ('colorSpace' in tex) tex.colorSpace = THREE.SRGBColorSpace;
      else if ('encoding' in tex) tex.encoding = THREE.sRGBEncoding;
      this.scene.environment = tex;
    }

    build() {
      this.group = new THREE.Group();
      this.scene.add(this.group);

      // Init impact slot data — 4 simultaneous ripples
      this._impactPosArr  = [
        new THREE.Vector3(0,0,0), new THREE.Vector3(0,0,0),
        new THREE.Vector3(0,0,0), new THREE.Vector3(0,0,0),
      ];
      this._impactTimeArr = [-1, -1, -1, -1];
      this._currentTime   = 0;

      // === Faceted gem — IcosahedronGeometry detail=1 (80 faces) ===
      // Material tuned to MATCH Peptide's rose-gold tone with
      // high-polish reflections.
      const gemGeo = new THREE.IcosahedronGeometry(1.30, 1);
      const gemMat = new THREE.MeshPhysicalMaterial({
        color:               0xC48E6E,    // rose gold (matches Peptide)
        metalness:           1.0,
        roughness:           0.06,
        clearcoat:           1.0,
        clearcoatRoughness:  0.04,
        emissive:            0x5A2F1F,
        emissiveIntensity:   0.18,
        envMapIntensity:     2.1,
        reflectivity:        1.0,
        flatShading:         true,
      });

      // Inject vertex shader for impact ripple — pushes vertices
      // outward briefly along their normals, oscillating like a
      // struck bell. Driven by uTime and a 4-slot impact buffer.
      gemMat.onBeforeCompile = (shader) => {
        shader.uniforms.uTime       = { value: 0 };
        shader.uniforms.uImpactPos  = { value: this._impactPosArr };
        shader.uniforms.uImpactTime = { value: this._impactTimeArr };

        shader.vertexShader = shader.vertexShader.replace(
          '#include <common>',
          `
          #include <common>
          uniform float uTime;
          uniform vec3  uImpactPos[4];
          uniform float uImpactTime[4];

          float impactDisp(vec3 pos) {
            vec3 nDir = normalize(pos);
            float total = 0.0;
            for (int i = 0; i < 4; i++) {
              if (uImpactTime[i] < 0.0) continue;
              float age = uTime - uImpactTime[i];
              if (age < 0.0 || age > 1.6) continue;
              vec3 imp = normalize(uImpactPos[i]);
              float ndotI = clamp(dot(nDir, imp), -1.0, 1.0);
              float arc = acos(ndotI);
              float spatial = exp(-pow(arc * 1.3, 2.0));
              float spring  = sin(age * 14.0 - arc * 5.0) * exp(-age * 3.5);
              total += spring * spatial;
            }
            return total * 0.18;
          }
          `
        );

        shader.vertexShader = shader.vertexShader.replace(
          '#include <begin_vertex>',
          `
          float disp = impactDisp(position);
          vec3 transformed = position + normalize(position) * disp;
          `
        );

        gemMat.userData.shader = shader;
      };

      this.gem = new THREE.Mesh(gemGeo, gemMat);
      this.group.add(this.gem);

      // Starfield — shared helper, IDENTICAL params across all 3 scenes
      // (sizeAttenuation: false → pixel-sized dots, camera-distance independent)
      const sf = buildStarField(this.scene);
      this.dust      = sf.group;
      this._dustNear = sf.near;
      this._dustFar  = sf.far;
    }

    update(t) {
      super.update(t);
      const tSec = t * 0.001;
      this._currentTime = tSec;

      // Process impact queue → fill empty slots (or recycle oldest)
      while (this._impactQueue.length > 0) {
        const impact = this._impactQueue.shift();
        let slot = 0;
        let oldest = Infinity;
        for (let i = 0; i < 4; i++) {
          if (this._impactTimeArr[i] < oldest) { oldest = this._impactTimeArr[i]; slot = i; }
        }
        this._impactPosArr[slot].copy(impact.pos);
        this._impactTimeArr[slot] = impact.time;
      }
      // Mark expired ripples inactive
      for (let i = 0; i < 4; i++) {
        if (this._impactTimeArr[i] >= 0 && tSec - this._impactTimeArr[i] > 1.6) {
          this._impactTimeArr[i] = -1;
        }
      }
      // Push uTime to the gem's shader (drives the impact ripple)
      const shader = this.gem.material.userData.shader;
      if (shader) shader.uniforms.uTime.value = tSec;

      // Auto rotation + mouse parallax tilt
      this.group.rotation.y += 0.0028;
      const tx = this.mouse.y * 0.30;
      const tz = this.mouse.x * 0.18;
      this.group.rotation.x += (tx - this.group.rotation.x) * 0.05;
      this.group.rotation.z += (tz - this.group.rotation.z) * 0.05;

      // Subtle breathing scale
      const breathe = 1 + Math.sin(tSec * 0.7) * 0.018;
      this.gem.scale.setScalar(breathe);

      // Dust drift parallax
      this._dustNear.rotation.y -= 0.00040;
      this._dustNear.rotation.x += 0.00012;
      this._dustFar.rotation.y  += 0.00018;
      this._dustFar.rotation.x  -= 0.00006;
    }
  }


  /* =================================================================
     Manager — instantiates scenes, drives the master loop
     ================================================================= */
  function init() {
    const peptideEl = document.getElementById('sci-canvas-peptide');
    const hydrateEl = document.getElementById('sci-canvas-hydrate');
    const protectEl = document.getElementById('sci-canvas-protect');
    if (!peptideEl || !hydrateEl || !protectEl) return;

    let scenes;
    try {
      scenes = [
        new PeptideScene(peptideEl),
        new HydrateScene(hydrateEl),
        new ProtectScene(protectEl),
      ];
    } catch (err) {
      console.warn('[science-renders] scene init failed:', err);
      return;
    }

    // Pause when section out of viewport
    let sectionVisible = true;
    const section = document.getElementById('how');
    if (section && 'IntersectionObserver' in window) {
      new IntersectionObserver((entries) => {
        sectionVisible = entries[0].isIntersecting;
      }, { rootMargin: '160px' }).observe(section);
    }

    /* §04 Ensemble — orbit-center peptide canvas. Independent of the
       HOW IT WORKS scenes: separate canvas, separate visibility check
       on the #ensemble section so it doesn't render when scrolled away. */
    let centerScene = null;
    let centerVisible = true;
    const centerCanvas = document.getElementById('orbit-peptide-canvas');
    if (centerCanvas) {
      try {
        centerScene = new PeptideCenterScene(centerCanvas);
        const btn = centerCanvas.closest('.orbit-center');
        if (btn) btn.classList.add('is-three-ready');
      } catch (err) {
        console.warn('[science-renders] orbit-center peptide init failed:', err);
        centerScene = null;
      }
      const ensembleSection = document.getElementById('ensemble');
      if (ensembleSection && 'IntersectionObserver' in window) {
        new IntersectionObserver((entries) => {
          centerVisible = entries[0].isIntersecting;
        }, { rootMargin: '160px' }).observe(ensembleSection);
      }
    }

    /* §04 Ensemble — peptide info-card hero. Persistent canvas + scene
       lives in a hidden offscreen "home" div. The panel script (in
       science.html) calls window.__sciHeroPeptide.attach(mountEl) to
       move the canvas into the panel's hero region whenever the peptide
       ingredient is shown, and .detach() before innerHTML rebuilds wipe
       the panel. Moving (not recreating) the canvas preserves the WebGL
       context across panel swaps. */
    let heroScene = null;
    let heroAttached = false;
    let heroHome = null;
    let heroCanvas = null;
    try {
      heroHome = document.createElement('div');
      heroHome.id = 'hero-peptide-home';
      heroHome.setAttribute('aria-hidden', 'true');
      heroHome.style.cssText =
        'position:absolute; left:-9999px; top:-9999px; width:560px; height:385px; pointer-events:none;';
      heroCanvas = document.createElement('canvas');
      heroCanvas.id = 'hero-peptide-canvas';
      heroCanvas.className = 'hero-peptide-canvas';
      heroCanvas.setAttribute('aria-hidden', 'true');
      heroCanvas.style.cssText =
        'width:100%; height:100%; display:block; pointer-events:none;';
      heroHome.appendChild(heroCanvas);
      document.body.appendChild(heroHome);
      // Hero card uses the same scene as the orbit-center — both inherit
      // the artistic peptide + acetate counterions from PeptideScene.
      // Tighter camera (no extra padding) so the molecule fills the
      // larger info-card canvas. The bounding-radius computation
      // already includes a 0.08 atom-edge buffer so this won't clip.
      heroScene = new PeptideCenterScene(heroCanvas);
      heroScene._paddingFactor = 1.00;
      heroScene.resize();
    } catch (err) {
      console.warn('[science-renders] hero peptide init failed:', err);
      heroScene = null;
    }

    window.__sciHeroPeptide = {
      attach(mountEl) {
        if (!heroScene || !mountEl || !heroCanvas) return;
        if (heroCanvas.parentNode !== mountEl) {
          mountEl.appendChild(heroCanvas);
          heroAttached = true;
          // Wait one frame for layout, then resize to match the new parent's dims
          requestAnimationFrame(() => heroScene.resize());
        }
      },
      detach() {
        if (!heroCanvas || !heroHome) return;
        if (heroCanvas.parentNode !== heroHome) {
          heroHome.appendChild(heroCanvas);
          heroAttached = false;
        }
      },
      available() { return !!heroScene; },
    };

    // Pause when tab inactive (save battery)
    let tabActive = !document.hidden;
    document.addEventListener('visibilitychange', () => {
      tabActive = !document.hidden;
    });

    // Master render loop
    function loop(t) {
      requestAnimationFrame(loop);
      if (!tabActive) return;
      if (sectionVisible) {
        for (let i = 0; i < scenes.length; i++) {
          const s = scenes[i];
          s.update(t);
          s.render();
        }
      }
      if (centerScene && centerVisible) {
        centerScene.update(t);
        centerScene.render();
      }
      if (heroScene && heroAttached) {
        heroScene.update(t);
        heroScene.render();
      }
    }
    requestAnimationFrame(loop);

    // Debounced resize for all scenes
    let rTimer;
    window.addEventListener('resize', () => {
      clearTimeout(rTimer);
      rTimer = setTimeout(() => {
        scenes.forEach((s) => s.resize());
        if (centerScene) centerScene.resize();
        if (heroScene) heroScene.resize();
      }, 120);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
